import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { TEMA, useMobil } from '../theme';
import { Startsida, AttGöra, hämtaStartsida, ändraUppgift, kr, datumText, felText } from '../api/inkop';
import { ungefär } from './InkopPlan';
import { sida, rubrik, etikett, länkknapp } from '../components/inkopStil';
import { ROSA, mono, nyckeltal, rad, ellips, Ruta, Läge, Rutnät, Personväljare } from '../components/Oversikt';

// Startsidan i Inköp är en översikt, inte en arbetsyta (beslut 3 nov 2026).
// Fem rutor, var och en med ett nyckeltal, en rad om läget och högst tre rader
// innehåll. Arbetet görs på sidorna som rutorna länkar till.
//
//   Att göra            antal, hur många som är försenade, de tre mest brådskande
//   Pengar ut           närmaste 30 dagarna, förfallet, nästa betalning
//   Inköpen just nu     antal pågående och värdet, per steg
//   Lager som tar slut  viner under gränsen, och de som saknar inköp eller plan
//   Kommande månader    de tre nästa månaderna ur Inköpsplanen

const ATT_GÖRA_VISAS = 3;
const MÅNAD_LÅNG = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];

// Datum i texterna från backend skrivs som "26 okt".
const medDatum = (text: string) => text.replace(/\d{4}-\d{2}-\d{2}/g, d => datumText(d, true));

function AttGöraRuta({ s, spann, onKlar }: { s: Startsida; spann: boolean; onKlar: (id: string) => void }) {
  const [alla, setAlla] = useState(false);
  const försenade = s.att_gora.filter(a => a.grupp === 'forsenat').length;
  const veckan = s.att_gora.filter(a => a.grupp === 'veckan').length;
  const visas = alla ? s.att_gora : s.att_gora.slice(0, ATT_GÖRA_VISAS);
  const läge = s.att_gora.length === 0 ? 'Inget att göra just nu.'
    : [försenade ? `${försenade} ${försenade === 1 ? 'försenad' : 'försenade'}` : null, veckan ? `${veckan} den här veckan` : null,
      s.att_gora.length - försenade - veckan ? `${s.att_gora.length - försenade - veckan} snart` : null].filter(Boolean).join(' · ');
  return (
    <Ruta titel="Att göra" spann={spann}>
      <div style={nyckeltal}>{s.att_gora.length}</div>
      <Läge text={läge} ton={försenade ? 'varning' : s.att_gora.length === 0 ? 'bra' : undefined} />
      {visas.map((a, n) => <AttGöraRad key={`${a.typ}-${a.lank}-${n}`} a={a} onKlar={onKlar} />)}
      {s.att_gora.length > ATT_GÖRA_VISAS && (
        <button style={{ ...länkknapp, fontSize: '12.5px', textAlign: 'left', paddingTop: '10px', borderTop: `1px solid ${TEMA.border}` }}
          onClick={() => setAlla(x => !x)}>
          {alla ? 'Visa färre' : `Visa alla ${s.att_gora.length}`}
        </button>
      )}
    </Ruta>
  );
}

function AttGöraRad({ a, onKlar }: { a: AttGöra; onKlar: (id: string) => void }) {
  return (
    <div style={{ ...rad, alignItems: 'center' }}>
      <Link to={a.lank} style={{ textDecoration: 'none', minWidth: 0, flex: 1 }}>
        <div style={{ ...ellips, color: TEMA.ink }}>{a.titel}</div>
        <div style={{ ...ellips, fontSize: '12px', color: a.grupp === 'forsenat' ? ROSA : TEMA.inkFaint, marginTop: '1px' }}>
          {medDatum(a.detalj)}{a.ansvarig ? ` · ${a.ansvarig}` : ''}
        </div>
      </Link>
      {a.uppgift_id ? (
        <button onClick={() => onKlar(a.uppgift_id!)} style={{
          background: 'transparent', border: `1px solid ${TEMA.border}`, borderRadius: '2px', color: TEMA.inkSoft,
          padding: '4px 10px', fontSize: '12px', cursor: 'pointer', fontFamily: "'Inter', sans-serif", flexShrink: 0,
        }}>Klar</button>
      ) : a.belopp_kr != null ? (
        <span style={{ ...mono, fontSize: '12.5px', color: TEMA.inkSoft, whiteSpace: 'nowrap' }}>{kr(a.belopp_kr)}</span>
      ) : null}
    </div>
  );
}

function PengarRuta({ s }: { s: Startsida }) {
  // Staplarna visar de kommande tolv veckorna, med förfallet inräknat i den här veckan.
  const framåt = s.veckor.filter(v => !v.nu ? new Date(v.start) > new Date() : true).slice(0, 12);
  const max = Math.max(...framåt.map(v => v.planerat), 1);
  const n = s.nasta_betalning;
  return (
    <Ruta titel="Pengar ut">
      <div style={nyckeltal}>≈ {ungefär(s.framat_30)}</div>
      <Läge
        text={s.forfallet ? `närmaste 30 dagarna, varav ≈ ${ungefär(s.forfallet)} förfallet` : 'närmaste 30 dagarna'}
        ton={s.forfallet ? 'varning' : undefined}
      />
      <div aria-hidden="true" style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '34px', marginBottom: '4px' }}>
        {framåt.map(v => (
          <div key={v.start} style={{ flex: 1, height: v.planerat ? `${Math.max((v.planerat / max) * 100, 8)}%` : '1px',
            background: v.planerat ? (v.nu ? ROSA : 'rgba(232,169,160,0.55)') : TEMA.border, borderRadius: '2px 2px 0 0' }} />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10.5px', color: TEMA.inkFaint, ...mono, marginBottom: '6px' }}>
        <span>nu</span><span>12 veckor</span>
      </div>
      {n && (
        <div style={{ ...rad, display: 'block' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
            <span style={{ color: TEMA.inkSoft, fontSize: '12.5px' }}>Nästa · {datumText(n.datum, true)}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {n.uppskattat && <span style={{ fontSize: '10.5px', color: TEMA.inkFaint, border: `1px solid ${TEMA.border}`, borderRadius: '3px', padding: '0 5px' }}>uppskattat</span>}
              <span style={{ ...mono, fontSize: '12.5px', color: TEMA.ink }}>{kr(n.kr)}</span>
            </span>
          </div>
          <div style={{ ...ellips, color: TEMA.ink, marginTop: '2px' }} title={n.text}>{n.text}</div>
        </div>
      )}
      <div style={{ ...rad, color: TEMA.inkSoft, fontSize: '12.5px' }}>
        <span>Sex månader framåt</span><span style={{ ...mono, color: TEMA.ink }}>≈ {ungefär(s.framat_6m)}</span>
      </div>
    </Ruta>
  );
}

function InköpRuta({ s }: { s: Startsida }) {
  const alla = s.steg.flatMap(st => st.inkop);
  const värde = alla.reduce((sum, i) => sum + (i.belopp_kr || 0), 0);
  const varningar = alla.filter(i => i.varning).length;
  const ejBokad = alla.filter(i => /transport ej bokad/.test(i.lage || '')).length;
  const varning = varningar ? `${varningar} med datum i fara`
    : ejBokad ? `${ejBokad} ${ejBokad === 1 ? 'bekräftat' : 'bekräftade'} utan bokad transport` : null;
  return (
    <Ruta titel="Inköpen just nu" länk="/inkop/lista" länktext="Alla inköp">
      <div style={nyckeltal}>{alla.length}</div>
      {/* Värdet står alltid på sin rad; en varning läggs under, i rosa. */}
      <div style={{ fontSize: '13px', marginTop: '4px', color: TEMA.inkSoft }}>{alla.length ? `pågående, ≈ ${ungefär(värde)} totalt` : 'Inga pågående inköp.'}</div>
      <Läge text={varning || ''} ton="varning" />
      {s.steg.map(st => {
        const summa = st.inkop.reduce((x, i) => x + (i.belopp_kr || 0), 0);
        return (
          <Link key={st.status} to="/inkop/lista" style={{ ...rad, color: st.inkop.length ? TEMA.ink : TEMA.inkFaint }}>
            <span>{st.namn}</span>
            <span style={{ display: 'flex', gap: '14px', ...mono, fontSize: '12.5px' }}>
              <span style={{ color: TEMA.inkSoft }}>{st.inkop.length ? `≈ ${ungefär(summa)}` : ''}</span>
              <span style={{ minWidth: '14px', textAlign: 'right' }}>{st.inkop.length}</span>
            </span>
          </Link>
        );
      })}
    </Ruta>
  );
}

function LagerRuta({ s }: { s: Startsida }) {
  const utan = s.lager_att_planera_antal;
  return (
    <Ruta titel="Lager som tar slut" länk="/inkop/plan" länktext="Planera">
      <div style={nyckeltal}>{s.lager_antal}</div>
      <Läge
        text={s.lager_antal === 0 ? `Inga viner under ${s.lagergrans} flaskor.`
          : utan ? `viner under ${s.lagergrans} fl, ${utan} utan inköp eller plan`
          : `viner under ${s.lagergrans} fl, alla har inköp eller plan`}
        ton={utan ? 'varning' : s.lager_antal ? 'bra' : undefined}
      />
      {s.lager_att_planera.map(l => (
        <Link key={l.artikel_nr} to={`/inkop/producenter/${l.producent_id}`} style={rad}>
          <span style={{ minWidth: 0 }}>
            <span style={{ ...ellips, display: 'block', color: TEMA.ink }}>{l.namn}</span>
            <span style={{ ...ellips, display: 'block', fontSize: '12px', color: TEMA.inkFaint }}>{l.producent}</span>
          </span>
          <span style={{ ...mono, fontSize: '12.5px', color: ROSA, whiteSpace: 'nowrap' }}>{l.lager} fl</span>
        </Link>
      ))}
    </Ruta>
  );
}

function KommandeRuta({ s }: { s: Startsida }) {
  const max = Math.max(...s.kommande.map(m => m.totalt), 1);
  const summa = s.kommande.reduce((x, m) => x + m.totalt, 0);
  return (
    <Ruta titel="Kommande månader" länk="/inkop/plan" länktext="Inköpsplanen">
      <div style={nyckeltal}>≈ {ungefär(summa)}</div>
      <Läge text={`de tre nästa månaderna, ${s.kommande.reduce((x, m) => x + m.poster.length, 0)} inköp`} />
      {s.kommande.map(m => (
        <Link key={m.manad} to="/inkop/plan" style={{ ...rad, display: 'block' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
            <span style={{ color: TEMA.ink }}>
              {MÅNAD_LÅNG[Number(m.manad.slice(5, 7)) - 1].replace(/^./, c => c.toUpperCase())}
              <span style={{ color: TEMA.inkFaint, fontSize: '12px' }}> · {m.poster.length} {m.poster.length === 1 ? 'inköp' : 'inköp'}</span>
            </span>
            <span style={{ ...mono, fontSize: '12.5px', color: TEMA.ink }}>{m.totalt ? `≈ ${ungefär(m.totalt)}` : '–'}</span>
          </div>
          <div aria-hidden="true" style={{ height: '3px', background: TEMA.border, borderRadius: '2px', marginTop: '6px' }}>
            <div style={{ width: `${(m.totalt / max) * 100}%`, height: '100%', background: TEMA.inkSoft, borderRadius: '2px' }} />
          </div>
        </Link>
      ))}
    </Ruta>
  );
}

export default function InkopStartsida() {
  const ärMobil = useMobil();
  const [s, setS] = useState<Startsida | null>(null);
  const [fel, setFel] = useState<string | null>(null);
  // Tomt = den inloggade. Admin kan växla till en annan person eller "alla".
  const [person, setPerson] = useState('');
  const [laddar, setLaddar] = useState(false);
  // Bara det senaste svaret visas, så att ett långsamt svar för Elsa inte
  // skriver över Alla när någon hunnit klicka vidare.
  const senaste = useRef(0);

  const ladda = useCallback(() => {
    const nr = ++senaste.current;
    setLaddar(true);
    hämtaStartsida(person || undefined)
      .then(r => { if (nr === senaste.current) { setS(r.data); setFel(null); } })
      .catch(e => { if (nr === senaste.current) setFel(felText(e, 'Kunde inte hämta startsidan.')); })
      .finally(() => { if (nr === senaste.current) setLaddar(false); });
  }, [person]);

  useEffect(() => { ladda(); }, [ladda]);

  async function klar(id: string) {
    try {
      await ändraUppgift(id, { klar: true });
      ladda();
      window.dispatchEvent(new Event('vinsales:att-atgarda-andrad'));
    } catch (err) {
      setFel(felText(err, 'Kunde inte bocka av.'));
    }
  }

  // Knappen markeras direkt vid klick, inte först när svaret kommit.
  const vald = person || (s ? (s.person?.id || 'alla') : '');
  const datum = new Date().toLocaleDateString('sv-SE', { weekday: 'long', day: 'numeric', month: 'long' }).toUpperCase();

  return (
    <div style={sida}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <div>
          <span style={etikett}>{datum}</span>
          <h1 style={rubrik}>Inköp startsida{s?.person?.namn ? <span style={{ color: TEMA.inkFaint, fontSize: '18px' }}> · {s.person.namn}</span> : null}
            {laddar && s && <span role="status" style={{ marginLeft: '12px', color: TEMA.inkFaint, fontSize: '13px', fontFamily: "'Inter', sans-serif" }}>Laddar...</span>}
          </h1>
        </div>
        {s?.admin && s.personer.length > 0 && (
          <Personväljare val={[...s.personer.map(p => ({ id: p.id, namn: p.namn || '' })), { id: 'alla', namn: 'Alla' }]} vald={vald} onVälj={setPerson} />
        )}
      </div>

      {fel && <p style={{ color: ROSA, fontSize: '13px' }}>{fel}</p>}
      {!s && !fel && <p>Laddar...</p>}

      {s && (
        <Rutnät ärMobil={ärMobil} laddar={laddar}>
          <AttGöraRuta s={s} spann={!ärMobil} onKlar={klar} />
          <PengarRuta s={s} />
          <InköpRuta s={s} />
          <LagerRuta s={s} />
          <KommandeRuta s={s} />
        </Rutnät>
      )}
    </div>
  );
}
